import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import UIOtpInput from 'components/core/otp-input/otp-input';
import { useToast } from 'hooks/use-toast';
import { User } from '../../../../../../src/types/user-type';
import {
  useGenerateOTP,
  useGetSetUpTotp,
  useVerifyTotpSetup,
} from '../../../hooks/use-mfa';
import QRCodeDummyImage from 'assets/images/image_off_placeholder.webp';
import { SetUpTotp } from '../../../types/mfa.types';
import { useConfig } from 'hooks/use-config';
import { useTranslation } from 'react-i18next';

/**
 * AuthenticatorAppSetup component allows the user to set up an authenticator app for two-factor authentication.
 * It retrieves a QR code that the user can scan or enter a manual setup key, followed by verifying the OTP from the authenticator app.
 *
 * @param {Object} props - The component props.
 * @param {User} [props.userInfo] - The user data, which contains information for setting up MFA (optional).
 * @param {Function} props.onClose - The function to close the dialog after cancel or successful verify.
 *
 * @returns {JSX.Element} The rendered component.
 */
type AuthenticatorAppSetupProps = {
  userInfo?: User;
  onClose: () => void;
};

const OTP_ERROR_DEFAULTS: Record<string, string> = {
  invalid_totp_code: 'Invalid verification code',
  invalid_two_factor_code: 'Invalid verification code',
  INVALID_VERIFICATION_CODE: 'Invalid verification code',
};

const getVerifyApiErrorKey = (source: {
  errors?: { message?: string };
  error?: unknown;
} | undefined) => {
  if (source?.errors?.message) return source.errors.message;
  if (typeof source?.error === 'string' && source.error) return source.error;
  return '';
};

export const AuthenticatorAppSetup: React.FC<Readonly<AuthenticatorAppSetupProps>> = ({
  userInfo,
  onClose,
}) => {
  const { toast } = useToast();
  const [otpValue, setOtpValue] = useState<string>('');
  const [otpError, setOtpError] = useState<string>('');
  const [isImageError, setIsImageError] = useState<boolean>(false);
  const [qrCodeUri, setQrCodeUri] = useState<string>('');
  const [manualQrCode, setManualQrCode] = useState<string>('');
  const lastVerifiedOtpRef = useRef<string>('');
  const { mutate: setUpTotp, isPending: setUpTotpPending } = useGetSetUpTotp();
  const { mutate: verifyTotpSetup, isPending: verifyOtpPending } = useVerifyTotpSetup();
  const { mutateAsync: generateOTPAsync } = useGenerateOTP();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  useEffect(() => {
    const userId = userInfo?.itemId;
    if (!userId) return;

    void generateOTPAsync({ userId, mfaType: 1 }).catch(() => undefined);
  }, [generateOTPAsync, userInfo?.itemId]);

  useEffect(() => {
    if (!userInfo) return;

    const setUpTotpPayload: SetUpTotp = {
      userId: userInfo?.itemId,
      projectKey: configurationSettings.blocksKey,
      OtpUploadtype: 1,
    };

    setUpTotp(setUpTotpPayload, {
      onSuccess: (response) => {
        if (response) {
          setQrCodeUri(response.qrImageUrl);
          setManualQrCode(response.qrCode);
        }
      },
      onError: () => {
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FAILED_TO_GENERATE_QR_CODE'),
        });
      },
    });
  }, [setUpTotp, toast, userInfo]);

  const isSetupReady = !setUpTotpPending && Boolean(qrCodeUri || manualQrCode);

  const onVerify = useCallback(() => {
    if (!isSetupReady) {
      toast({
        variant: 'destructive',
        title: t('SETUP_INCOMPLETE'),
        description: t('PLEASE_GENERATE_QR_CODE_FIRST'),
      });
      return;
    }

    const userId = userInfo?.itemId;
    if (!userId) {
      toast({
        variant: 'destructive',
        title: t('SETUP_INCOMPLETE'),
        description: t('PLEASE_GENERATE_QR_CODE_FIRST'),
      });
      return;
    }

    verifyTotpSetup(
      { code: otpValue, userId },
      {
        onSuccess: (res) => {
          const errorKey = getVerifyApiErrorKey(res);
          const isRejected =
            Boolean(errorKey) ||
            res?.isSuccess === false ||
            (res?.isValid === false && res?.isSuccess !== true) ||
            res?.valid === false;
          if (isRejected) {
            setOtpError(errorKey || 'INVALID_VERIFICATION_CODE');
            return;
          }
          toast({
            variant: 'success',
            title: t('AUTHENTICATOR_LINKED_SUCCESS', 'Authenticator app added successfully.'),
          });
          onClose();
        },
        onError: (error) => {
          const body = error?.error as
            | { errors?: { message?: string }; error?: string }
            | undefined;
          setOtpError(getVerifyApiErrorKey(body) || 'INVALID_VERIFICATION_CODE');
        },
      }
    );
  }, [isSetupReady, onClose, otpValue, toast, userInfo?.itemId, verifyTotpSetup, t]);

  useEffect(() => {
    if (
      otpValue.length === 6 &&
      isSetupReady &&
      !verifyOtpPending &&
      otpValue !== lastVerifiedOtpRef.current
    ) {
      lastVerifiedOtpRef.current = otpValue;
      onVerify();
    }
  }, [onVerify, otpValue, isSetupReady, verifyOtpPending]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent nested hideClose className="rounded-md sm:max-w-[432px] overflow-y-auto max-h-screen">
        <DialogHeader>
          <DialogTitle>{t('SET_UP_AUTHENTICATOR_APP')}</DialogTitle>
          <DialogDescription>{t('FOLLOW_INSTRUCTIONS')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col w-full gap-4">
          <div className="flex w-full text-high-emphasis text-sm gap-1 font-normal">
            <span>1.</span>
            <span>{t('SCAN_QR_CODE_OR_ENTER_MANUAL_KEY')}</span>
          </div>
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="w-40 h-40 border border-border rounded-[8px] p-2">
              {!setUpTotpPending ? (
                <img
                  src={qrCodeUri && !isImageError ? qrCodeUri : QRCodeDummyImage}
                  alt="otp qr code"
                  className="w-full h-full object-cover"
                  onError={() => setIsImageError(true)}
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-center flex-col gap-2">
              <p className="text-medium-emphasis text-center font-normal">
                {t('OR_ENTER_MANUAL_KEY')}:
              </p>
              <p className="text-center text-sm font-semibold text-high-emphasis">
                {manualQrCode ?? ''}
              </p>
            </div>
          </div>
          <div className="flex w-full text-high-emphasis text-sm gap-1 font-normal">
            <span>2.</span>
            <span>{t('VERIFY_PAIRING_SUCCESSFUL')}</span>
          </div>
          <div className="flex flex-col gap-1">
            <UIOtpInput
              value={otpValue}
              inputStyle={otpError ? '!border-error !text-destructive' : ''}
              onChange={(value) => {
                setOtpValue(value);
                setOtpError('');
              }}
            />
            {otpError && (
              <span className="text-destructive text-xs">
                {t(otpError, OTP_ERROR_DEFAULTS[otpError] ?? 'Invalid verification code')}
              </span>
            )}
          </div>
        </div>
        <DialogFooter className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="min-w-[118px]">
            {t('CANCEL')}
          </Button>
          <Button
            onClick={onVerify}
            disabled={verifyOtpPending || otpValue.length < 6}
            className="min-w-[118px]"
          >
            {verifyOtpPending ? t('VERIFYING') : t('VERIFY')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
